import { EventEmitter, Injectable, Output, ViewContainerRef } from '@angular/core';
import { ResourceManager } from 'libs/abc/core/resources/src';
import {
  AbcComponentService,
  AbcContextMenu,
  AbcGridComponent,
  AbcNotificationComponent,
  INotification,
  ToastType,
} from 'libs/abc/ui-components/src';
import { gridOptions } from '../../../core/src/abc-grid/abc-grid';
import { AbcContainerService } from '../abcContainer.service';
import {
  AbcGridColumnsLine,
  AbcGridDefaultContextMenu,
  AbcGridDefaultContextMenuIconsPath,
  AbcGridGroupBy,
} from './abcGridContextMenu.model';
const res = ResourceManager.getResources('abc-ui-components-abcGrid');
@Injectable({
  providedIn: 'root',
})
export class AbcGridContextMenuService {
  @Output() onColumnDrag = new EventEmitter();
  abcNotification: ViewContainerRef;
  private readonly clipBoardHttpsError = { type: ToastType.Error, message: res('ContextMenu-ClipBoard-HttpsError') };
  private readonly clipBoardPermissionDenied = {
    type: ToastType.Warning,
    message: res('ContextMenu-ClipBoard-PermissionDenied'),
  };

  constructor(private abcComponentService: AbcComponentService, private abcContainer: AbcContainerService) {}

  private gridAttributes = {
    filterBuilder: false,
    multipleSelection: false,
    columnResizing: true,
    summary: false,
  };
  private clipBoardText;
  private contextMenu: AbcGridDefaultContextMenu = new AbcGridDefaultContextMenu();
  public gridInstance: AbcGridComponent;
  public gridData: any;
  public columnHeaderName: string;
  public columnHeaderIndex: number;
  public defaultGridContextMenu: AbcContextMenu[] = [
    {
      id: 'ContextMenu-RemoveSorts',
      text: this.contextMenu.rmvSort,
      disabled: true,
      icon: AbcGridDefaultContextMenuIconsPath.rmvSort,
      onItemClick: () => {
        this.gridInstance.removeSort();
      },
    },
    {
      id: 'ContextMenu-AddNewDataColumn',
      text: this.contextMenu.addcolumn,
      icon: AbcGridDefaultContextMenuIconsPath.addColumn,
      onItemClick: () => {
        this.gridInstance.addEmptyRow();
      },
      disabled: true,
      visible: false,
    },
    {
      id: 'ContextMenu-ColumnChooser',
      text: this.contextMenu.columnselection,
      icon: AbcGridDefaultContextMenuIconsPath.columnSelection,
      onItemClick: () => {
        this.addRemoveColumns();
      },
    },
    {
      id: 'ContextMenu-PasteValueFromClipboard',
      text: this.contextMenu.paste,
      icon: AbcGridDefaultContextMenuIconsPath.paste,
      onItemClick: () => {
        this.pasteDataFromClipBoard();
      },
    },
    {
      id: 'ContextMenu-CopyColumnToClipboard',
      text: this.contextMenu.copy,
      icon: AbcGridDefaultContextMenuIconsPath.copy,
      onItemClick: () => {
        this.copyDataToClipBoard(this.columnHeaderName);
      },
    },
    {
      id: 'ContextMenu-ExportToExcel',
      text: this.contextMenu.excel,
      icon: AbcGridDefaultContextMenuIconsPath.excel,
      onItemClick: () => {
        this.exportGridData();
      },
    },
    {
      id: 'ContextMenu-MoreOptions',
      text: this.contextMenu.more,
      icon: AbcGridDefaultContextMenuIconsPath.more,
      items: [
        {
          id: 'ContextMenu-GroupBy',
          text: this.contextMenu.group.enable,
          icon: AbcGridDefaultContextMenuIconsPath.groupBy,
          onItemClick: () => {
            this.gridInstance.groupable = this.enableOrDisableOptions(
              this.gridInstance.groupable,
              this.contextMenu.group
            );
          },
        },
        {
          id: 'ContextMenu-HeaderFilter',
          text: this.contextMenu.filterHeader.enable,
          icon: AbcGridDefaultContextMenuIconsPath.headerFilter,
          onItemClick: () => {
            this.gridInstance.columnFilter = this.enableOrDisableOptions(
              this.gridInstance.columnFilter,
              this.contextMenu.filterHeader
            );
          },
        },
        {
          id: 'ContextMenu.FilterBuilder',
          text: this.contextMenu.filterBuilder.enable,
          icon: AbcGridDefaultContextMenuIconsPath.filterBuilder,
          onItemClick: () => {
            this.gridInstance.filterPanel = this.enableOrDisableOptions(
              this.gridInstance.filterPanel,
              this.contextMenu.filterBuilder
            );
          },
        },
        {
          id: 'ContextMenu.FilterRow',
          text: this.contextMenu.filterRow.disable,
          icon: AbcGridDefaultContextMenuIconsPath.filterRow,
          onItemClick: () => {
            this.gridInstance.showfilterrow = this.enableOrDisableOptions(
              this.gridInstance.showfilterrow,
              this.contextMenu.filterRow
            );
          },
        },
        {
          id: 'ContextMenu.Selection',
          visible: false,
          text: this.contextMenu.selectionMode.enable,
          icon: AbcGridDefaultContextMenuIconsPath.multiSelection,
          onItemClick: () => {
            this.gridAttributes.multipleSelection = this.enableOrDisableOptions(
              this.gridAttributes.multipleSelection,
              this.contextMenu.selectionMode
            );
          },
        },
        {
          id: 'ContextMenu.TotalCount',
          text: this.contextMenu.totalCount.enable,
          icon: AbcGridDefaultContextMenuIconsPath.totalCount,
          onItemClick: () => {
            this.gridAttributes.summary = this.enableOrDisableOptions(
              this.gridAttributes.summary,
              this.contextMenu.totalCount
            );
          },
        },
        {
          id: 'ContextMenu.ColumnResizing',
          text: this.contextMenu.columnResizing.disable,
          icon: AbcGridDefaultContextMenuIconsPath.groupBy,
          onItemClick: () => {
            this.gridInstance.columnsresize = this.enableOrDisableOptions(
              this.gridInstance.columnsresize,
              this.contextMenu.columnResizing
            );
          },
        },
      ],
    },
  ];

  public copyDataToClipBoard(columnHeaderName: string): void {
    const hignaGrid: AbcGridComponent = this.abcComponentService.getInstanceById(this.gridInstance.id);
    let dataToCopy = hignaGrid.copyColumnDataToClipBoard(columnHeaderName);
    if (Array.isArray(dataToCopy)) {
      dataToCopy = dataToCopy.join('\n');
    }
    this.clipBoardText = dataToCopy;
    const selBox = document.createElement('textarea');
    selBox.value = dataToCopy;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  public exportGridData(): void {
    this.gridInstance.exportGridData();
  }

  public setPasteDataText(): void {
    this.defaultGridContextMenu[DefaultOptionsPosition.pasteData].text = this.contextMenu.paste;
  }

  public pasteDataFromClipBoard(): void {
    if (this.gridAttributes.filterBuilder === false) {
      const permissionName = 'clipboard-read' as PermissionName;
      navigator.permissions
        .query({ name: permissionName })
        .then((result) => {
          if (result.state === 'granted') {
            navigator.clipboard
              .readText()
              .then((text) => {
                this.prepareDataToPaste(text);
              })
              .catch((err) => {
                console.error('Failed to read clipboard contents: ', err);
              });
          } else {
            this.showNotificationToast(this.clipBoardPermissionDenied);
            if (this.clipBoardText) this.prepareDataToPaste(this.clipBoardText);
          }
        })
        .catch((err) => {
          this.showNotificationToast(this.clipBoardHttpsError);
        });
    } else {
      this.gridInstance.setGridOptions(gridOptions.filterPanel, false);
      this.defaultGridContextMenu[DefaultOptionsPosition.pasteData].text = this.contextMenu.paste;
      this.gridInstance.contextMenuOptions = this.defaultGridContextMenu;
      this.gridAttributes.filterBuilder = false;
    }
  }

  private prepareDataToPaste(text): void {
    const hignaGrid: AbcGridComponent = this.abcComponentService.getInstanceById(this.gridInstance.id);
    this.defaultGridContextMenu[DefaultOptionsPosition.pasteData].text = this.contextMenu.filterBuilder.disable;
    this.gridInstance.contextMenuOptions = this.defaultGridContextMenu;
    let dataToAdd = text.toString().split('\n');
    hignaGrid.pasteDataFromClipBoard(this.columnHeaderName, dataToAdd, this.columnHeaderIndex);
    this.gridAttributes.filterBuilder = true;
  }

  public enableOrDisableOptions(propertyValue: boolean, optionText): boolean {
    if (propertyValue) {
      this.gridInstance.setGridOptions(optionText.name, false);
      this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[optionText.position].text = optionText.enable;
      this.enableSpecialOptions(optionText.name, false, AbcGridColumnsLine.None, AbcGridGroupBy.None);
      propertyValue = false;
    } else {
      this.gridInstance.setGridOptions(optionText.name, true);
      this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[optionText.position].text =
        optionText.disable;
      this.enableSpecialOptions(optionText.name, true, AbcGridColumnsLine.Line, AbcGridGroupBy.Block);
      propertyValue = true;
    }
    return propertyValue;
  }
  public addRemoveColumns(): void {
    this.onColumnDrag.emit();
  }

  private setGridColumnLine(value: AbcGridColumnsLine): void {
    const columnHeader = document.querySelectorAll('.dx-datagrid-headers .dx-datagrid-table .dx-row > td');
    columnHeader.forEach((element) => {
      const e = element as HTMLElement;
      e.style.borderRight = value;
    });
  }

  private enableSpecialOptions(
    optionName: gridOptions,
    summary: boolean,
    resize: AbcGridColumnsLine,
    groupby: AbcGridGroupBy
  ): void {
    const groupDiv = document.querySelector('.dx-datagrid-header-panel') as HTMLElement;
    if (optionName === gridOptions.groupable) {
      groupDiv.style.display = groupby;
    }
    if (optionName === gridOptions.summaryTotalCount) {
      this.gridInstance.updateSummary(summary);
    }
    if (optionName === gridOptions.columnResize) {
      this.setGridColumnLine(resize);
    }
  }

  public checkGridColumnLine(): void {
    if (!this.gridInstance.columnsresize) {
      this.setGridColumnLine(AbcGridColumnsLine.None);
    }
  }

  private showNotificationToast(message): void {
    const notifAttr: INotification = {
      id: 'error-notification',
      message: message.message,
      type: message.type,
      displayTime: message.type === 'error' ? 8000 : 4000,
    };
    this.abcContainer.createComponent(this.abcNotification, AbcNotificationComponent, notifAttr).componentRef.instance;
  }

  public resetContextMenu(): void {
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.groupBy].text =
      this.contextMenu.group.enable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.headerFilter].text =
      this.contextMenu.filterHeader.enable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.filterBuilder].text =
      this.contextMenu.filterBuilder.enable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.filterRow].text =
      this.contextMenu.filterRow.disable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.multiSelection].text =
      this.contextMenu.selectionMode.enable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.totalCount].text =
      this.contextMenu.totalCount.enable;
    this.defaultGridContextMenu[DefaultOptionsPosition.moreItems].items[DefaultOptionsPosition.columnResizing].text =
      this.contextMenu.columnResizing.disable;
    this.gridAttributes = {
      filterBuilder: false,
      multipleSelection: false,
      columnResizing: true,
      summary: false,
    };
  }
}

export const enum DefaultOptionsPosition {
  groupBy = 0,
  removeSort = 0,
  addColumn = 1,
  headerFilter = 1,
  filterBuilder = 2,
  pasteData = 3,
  filterRow = 3,
  multiSelection = 4,
  totalCount = 5,
  columnResizing = 6,
  moreItems = 6,
}
